import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { ConfigContext } from '../../../../context';
import { StoryCardStatus, User } from '../../../../services';
import { FileAcceptType, useTags } from '../../../hooks';
import { GalleryItem } from '../../../models';
import { generateRandomString, getCssVar, isEmptyString } from '../../../utils';
import { CloseButton } from '../../CloseButton';
import { FilePreview, FileUpload } from '../../File';
import { IconLabel } from '../../IconLabel';
import { InputField } from '../../InputField';
import { ScheduleInput } from '../../ScheduleInput';
import { Switch } from '../../Switch';
import { TextAreaField } from '../../TextAreaField';
import { UserMentionPreview } from '../../UserMentionPreview';
import { CreateCardSettingsTabType, PollPostFields } from '../CreateCardModal';
import { PostToggleFields, PostToggleFieldsItem } from '../PostToggleFields';

import classNames from 'classnames';
import classes from './PollPost.module.scss';

const MIN_POLL_OPTIONS = 2;
const MAX_POLL_OPTIONS = 12;

interface PollPostProps {
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  pollPostFields: PollPostFields;
  setPollPostFields: Dispatch<SetStateAction<PollPostFields>>;
  setPollPostGalleryItems: (galleryItems: GalleryItem[]) => void;
  setSettingsOpen: (isOpen: boolean) => void;
  setActivePollPostSettingsTabIndex: (index: number) => void;
  mentionedUsers: User[];
  setUserMentionModalOpen: Dispatch<SetStateAction<boolean>>;
  postInStoryAllowMentions: boolean;
  cardStatus?: StoryCardStatus;
}

type PollPostToggleFields = {
  [key in keyof PollPostFields]?: PostToggleFieldsItem;
};

export const PollPost: FunctionComponent<PollPostProps> = memo(
  ({
    loading,
    setLoading,
    pollPostFields,
    setPollPostFields,
    setPollPostGalleryItems,
    setSettingsOpen,
    setActivePollPostSettingsTabIndex,
    mentionedUsers,
    setUserMentionModalOpen,
    postInStoryAllowMentions,
    cardStatus,
  }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'addContent' });

    const { hasContentTags } = useTags();

    const { showAllowAnonymous } = useContext(ConfigContext).config.elements.addContent.poll;

    const {
      headline,
      text,
      options,
      allowAnonymous,
      totalVotes,
      showTotalVotes,
      multipleVotes,
      showVoteResults,
      votingTime,
      gallery,
    } = pollPostFields;

    const titleRef = useRef<HTMLTextAreaElement>(null);

    const textRef = useRef<HTMLTextAreaElement>(null);

    const [postFieldsToggling, setPostFieldsToggling] = useState<PollPostToggleFields>({
      text: {
        ref: textRef,
        title: ctxT('poll-post-text'),
        iconId: '',
        hidden: Boolean(isEmptyString(text)),
      },
    });

    const hasVotesOrPublished = useMemo(
      () => totalVotes > 0 || cardStatus === StoryCardStatus.PUBLISHED,
      [cardStatus, totalVotes]
    );

    const inputDisabled = useMemo(
      () => hasVotesOrPublished || loading,
      [hasVotesOrPublished, loading]
    );

    const hasGallery = useMemo(() => Boolean(gallery.length), [gallery.length]);

    const acceptTypes = useMemo(() => {
      return hasGallery ? [FileAcceptType.IMAGE] : [FileAcceptType.IMAGE, FileAcceptType.VIDEO];
    }, [hasGallery]);

    const multiUpload = useMemo(
      () => Boolean(hasGallery && gallery.find(({ image }) => image)),
      [gallery, hasGallery]
    );

    const uploadLimit = useMemo(() => {
      if (!hasGallery) {
        return;
      }

      return Boolean(gallery[0].video) ? 1 : 20;
    }, [gallery, hasGallery]);

    const keyDownHandler = useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>, field: keyof PollPostFields) => {
        const { key, target } = event;

        if (key === 'Backspace' && !(target as HTMLTextAreaElement).value) {
          setPostFieldsToggling({
            ...postFieldsToggling,
            [field]: { ...postFieldsToggling[field], hidden: true },
          });
        }
      },
      [postFieldsToggling]
    );

    const fieldChangeHandler = useCallback(
      (name: keyof PollPostFields, value: Date | string | boolean | null) => {
        setPollPostFields({ ...pollPostFields, [name]: value });
      },
      [setPollPostFields, pollPostFields]
    );

    const optionChangeHandler = useCallback(
      (id: string, text: string) => {
        setPollPostFields((pollPostFieldsPrev) => {
          const { options: optionsPrev } = pollPostFieldsPrev;

          const options = optionsPrev.map((option) =>
            option.id === id ? { ...option, text } : option
          );

          return { ...pollPostFieldsPrev, options };
        });
      },
      [setPollPostFields]
    );

    const addOptionHandler = useCallback(() => {
      setPollPostFields((pollPostFieldsPrev) => {
        return {
          ...pollPostFieldsPrev,
          options: [...pollPostFieldsPrev.options, { id: generateRandomString(4), text: '' }],
        };
      });
    }, [setPollPostFields]);

    const removeOptionHandler = useCallback(
      (id: string) => {
        setPollPostFields((pollPostFieldsPrev) => {
          const { options: optionsPrev } = pollPostFieldsPrev;

          const options = optionsPrev.filter((option) => option.id !== id);

          return { ...pollPostFieldsPrev, options };
        });
      },
      [setPollPostFields]
    );

    const tagsClickHandler = useCallback(() => {
      setActivePollPostSettingsTabIndex(CreateCardSettingsTabType.TAGS);
      setSettingsOpen(true);
    }, [setActivePollPostSettingsTabIndex, setSettingsOpen]);

    useEffect(() => {
      if (hasVotesOrPublished) {
        return;
      }

      const title = titleRef.current;

      if (!title) {
        return;
      }

      title.setSelectionRange(title.value.length, title.value.length);

      setTimeout(() => title.focus(), 0);
    }, [hasVotesOrPublished]);

    return (
      <div
        className={classNames(classes['poll-post'], {
          [classes['poll-post--mobile']]: isMobileOnly,
        })}
      >
        <div
          className={classNames(classes['poll-post__scrollable'], {
            [classes['poll-post__scrollable--mobile']]: isMobileOnly,
          })}
        >
          {hasVotesOrPublished && (
            <IconLabel
              iconId={'alert'}
              label={ctxT('poll-edit-message')}
              iconSize={18}
              singleColor
              nonClickable
            />
          )}

          <TextAreaField
            ref={titleRef}
            inputClassName={classes['poll-post__headline']}
            value={headline}
            placeholder={ctxT('poll-post-headline')}
            maxLength={360}
            onChange={({ target }) => fieldChangeHandler('headline', target.value)}
            disabled={inputDisabled}
            disableLineBreak
          />

          <UserMentionPreview
            mentionedUsers={mentionedUsers}
            {...(postInStoryAllowMentions && { onClick: () => setUserMentionModalOpen(true) })}
          />

          {!hasVotesOrPublished && (
            <>
              <FileUpload
                galleryItems={gallery}
                setGalleryItems={setPollPostGalleryItems}
                loading={loading}
                setLoading={setLoading}
                fileAcceptTypes={acceptTypes}
                uploadLimit={uploadLimit}
                multiUpload={multiUpload}
              />

              <FilePreview
                galleryItems={gallery}
                setGalleryItems={setPollPostGalleryItems}
                hasSettings
              />
            </>
          )}

          <TextAreaField
            ref={textRef}
            inputClassName={classes['poll-post__text']}
            value={text}
            placeholder={ctxT('poll-post-text')}
            maxLength={700}
            onChange={({ target }) => fieldChangeHandler('text', target.value)}
            onKeyDown={(event) => keyDownHandler(event, 'text')}
            hidden={postFieldsToggling?.text?.hidden}
            disabled={inputDisabled}
          />

          {options.map(({ id, text }, index) => {
            return (
              <div key={id} className={classes['poll-post__option']}>
                <InputField
                  label={`${ctxT('poll-post-option')} ${index + 1}${index < 2 ? '*' : ''}`}
                  value={text}
                  onChange={({ target }) => optionChangeHandler(id, target.value)}
                  maxLength={100}
                  disabled={inputDisabled}
                  autoFocus
                />

                {!hasVotesOrPublished && index >= MIN_POLL_OPTIONS && (
                  <CloseButton
                    className={classes['poll-post__option-close']}
                    onClick={() => removeOptionHandler(id)}
                    ariaLabel={t('common.remove')}
                    disabled={loading}
                  />
                )}
              </div>
            );
          })}

          {!hasVotesOrPublished && options.length < MAX_POLL_OPTIONS && (
            <IconLabel
              iconId={'add-plus'}
              iconSize={18}
              label={ctxT('add-option')}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
              onClick={addOptionHandler}
              disabled={loading}
            />
          )}

          <div className={classes['poll-post__switches']}>
            {!hasVotesOrPublished && (
              <>
                <Switch
                  label={ctxT('poll-post-multiple-votes')}
                  checked={multipleVotes}
                  onChange={() => fieldChangeHandler('multipleVotes', !multipleVotes)}
                  switchFirst
                />

                {showAllowAnonymous && (
                  <Switch
                    label={ctxT('poll-post-allow-anonymous')}
                    checked={allowAnonymous}
                    onChange={() => fieldChangeHandler('allowAnonymous', !allowAnonymous)}
                    switchFirst
                  />
                )}
              </>
            )}

            <div className={classes['poll-post__switches-row']}>
              <Switch
                label={ctxT('poll-post-show-vote-results')}
                checked={showVoteResults}
                onChange={() => fieldChangeHandler('showVoteResults', !showVoteResults)}
                switchFirst
              />

              {showVoteResults && (
                <Switch
                  label={ctxT('poll-post-show-total-votes')}
                  checked={showTotalVotes}
                  onChange={() => fieldChangeHandler('showTotalVotes', !showTotalVotes)}
                  switchFirst
                />
              )}
            </div>

            {!hasVotesOrPublished && (
              <ScheduleInput
                time={votingTime ? new Date(votingTime) : null}
                setTime={(time) => fieldChangeHandler('votingTime', time)}
                title={ctxT('poll-voting-time-title')}
                subTitle={ctxT('poll-voting-time-description')}
                iconLabel={ctxT('poll-voting-time-label')}
              />
            )}
          </div>
        </div>
        <div className={classes['poll-post__pane']}>
          <div className={classes['poll-post__pane-fields']}>
            {postInStoryAllowMentions && (
              <IconLabel
                iconId={'mention'}
                iconSize={20}
                label={ctxT('mention')}
                color={getCssVar('--create-card-link-color')}
                hoverColor={getCssVar('--create-card-link-hover-color')}
                onClick={() => setUserMentionModalOpen(true)}
              />
            )}
            {hasContentTags && (
              <IconLabel
                iconId={'tags'}
                iconSize={20}
                label={ctxT('tags')}
                color={getCssVar('--create-card-link-color')}
                hoverColor={getCssVar('--create-card-link-hover-color')}
                onClick={tagsClickHandler}
              />
            )}
            {!hasVotesOrPublished && (
              <PostToggleFields
                loading={loading}
                fields={postFieldsToggling}
                setFields={setPostFieldsToggling}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);
